import { useCallback, useEffect, useState } from 'react';

import { getTagReasons } from '../services/tags';

/**
 * WHY AN ITEM CARRIES ITS AUTOMATIC TAGS (docs/plans/tags.md §3.4 "Why";
 * keepr-api `GET /api/items/{id}/tag-reasons`, T6a): one request per item and
 * `version` (the item's `updatedAt` — a write can change what a rule found),
 * asked when a "Why" pop-over first opens, never for an item nobody asks
 * about. An answer for an item or version the caller has moved on from is
 * dropped. No cache across mounts: a pop-over keeps its content once opened.
 *
 * → `{ reasons, loading, error, reload }` (`reload` asks again after a failure), `reasons` as the API answers them
 * (`[{ tagId, since, facts }]`).
 */
export default function useTagReasons(itemId, version = null) {
	const [state, setState] = useState({ reasons: [], loading: Boolean(itemId), error: null });
	const [attempt, setAttempt] = useState(0);

	useEffect(() => {
		if (!itemId) {
			setState({ reasons: [], loading: false, error: null });
			return undefined;
		}
		let active = true;
		setState(prev => ({ ...prev, loading: true, error: null }));
		getTagReasons(itemId)
			.then(reasons => { if (active) setState({ reasons, loading: false, error: null }); })
			.catch(error => { if (active) setState({ reasons: [], loading: false, error }); });
		return () => { active = false; };
	}, [itemId, version, attempt]);

	const reload = useCallback(() => setAttempt(n => n + 1), []);
	return { ...state, reload };
}
